import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";

import useUserInfoQuery from "../../hooks/queries/useUserInfoQuery";
import useCategoriesQuery from "../../hooks/queries/useCategoriesQuery";
import useWebsiteConfigurationQuery from "../../hooks/queries/useWebsiteConfigurationQuery";
import accountActions from "../../store/actions/accounts/account.actions";
import { accountActionTypes } from "../../store/actionTypes/accounts/account.actionTypes";
import { categoryActionTypes } from "../../store/actionTypes/items/categories/category.actionTypes";
import { websiteConfigurationActionTypes } from "../../store/actionTypes/websiteConfiguration/websiteConfiguration.actionTypes";

// 0015: the SINGLE place that fetches the shared bootstrap resources (UserInfo, categories,
// websiteConfiguration). Each goes through React Query (dedup + cache) and is bridged into Redux
// via the EXISTING *_SUCCESS/_FAILURE actions, so every existing consumer + route guard keeps
// reading from Redux unchanged. Mounted ONCE in App. Renders nothing.
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    return jwtDecode(token).exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const AppBootstrap = () => {
  const dispatch = useDispatch();
  const { token, isAuthenticated } = useSelector((state) => state.accountReducer);
  const tokenValid = isTokenValid(token);

  // 0034: fetch the Enable accessibility script URL from backend config and inject it dynamically.
  // Uses raw fetch (not appHttpClient) to avoid the 401 interceptor on this anonymous endpoint.
  // Silent catch — the widget is non-critical; failure must not affect the app.
  useEffect(() => {
    fetch('/api/PublicConfig')
      .then((res) => res.json())
      .then((data) => {
        if (data.enableScriptUrl) {
          const script = document.createElement('script');
          script.src = data.enableScriptUrl;
          script.async = true;
          document.body.appendChild(script);
        }
      })
      .catch(() => {});
  }, []);

  // Preserve useAccount's former side effect: an authenticated-but-EXPIRED token logs out.
  useEffect(() => {
    if (isAuthenticated && !tokenValid) {
      dispatch(accountActions.logout());
    }
  }, [isAuthenticated, tokenValid, dispatch]);

  // UserInfo is auth-gated; categories + websiteConfiguration are anonymous-reachable (NOT gated).
  const userInfoQuery = useUserInfoQuery({ token, enabled: isAuthenticated && tokenValid });
  const categoriesQuery = useCategoriesQuery();
  const configQuery = useWebsiteConfigurationQuery();

  // --- Bridge React Query -> Redux. RQ v5 has no onSuccess; effect on data/error instead. ---
  useEffect(() => {
    if (userInfoQuery.data) {
      dispatch({ type: accountActionTypes.GET_USER_INFO_SUCCESS, data: userInfoQuery.data });
    }
  }, [userInfoQuery.data, dispatch]);
  useEffect(() => {
    if (userInfoQuery.error) {
      dispatch({ type: accountActionTypes.GET_USER_INFO_FAILURE, error: userInfoQuery.error });
    }
  }, [userInfoQuery.error, dispatch]);

  useEffect(() => {
    if (categoriesQuery.data) {
      dispatch({ type: categoryActionTypes.GET_CATEGORIES_SUCCESS, data: categoriesQuery.data });
    }
  }, [categoriesQuery.data, dispatch]);
  useEffect(() => {
    if (categoriesQuery.error) {
      dispatch({ type: categoryActionTypes.GET_CATEGORIES_FAILURE, error: categoriesQuery.error });
    }
  }, [categoriesQuery.error, dispatch]);

  useEffect(() => {
    if (configQuery.data) {
      dispatch({ type: websiteConfigurationActionTypes.GET_ALL_CONFIGURATIONS_SUCCESS, data: configQuery.data });
    }
  }, [configQuery.data, dispatch]);
  useEffect(() => {
    if (configQuery.error) {
      dispatch({ type: websiteConfigurationActionTypes.GET_ALL_CONFIGURATIONS_FAILURE, error: configQuery.error });
    }
  }, [configQuery.error, dispatch]);

  return null;
};

export default AppBootstrap;
